import { useCallback, useState } from 'react';
import {
  KoForm,
  KoFormBuilder,
  KoInput,
  KoText,
  KoButton,
  KoToast
} from 'packages';
import KoIcon from '@kwapio/icons';
import { ReleaseAPI } from '../../services/release.service';
import styles from './crud.module.scss';

const PROVIDER_META = {
  github: {
    label: 'GitHub',
    icon: 'GithubOutlined',
    color: '#24292e',
    oauthLabel: 'Continue with GitHub OAuth',
    patLabel: 'GitHub Personal Access Token',
    patPlaceholder: 'ghp_xxxxxxxxxxxxxxxxxxxx',
    patHint: 'Settings > Developer settings > Personal access tokens > Generate new token (repo, read:org scopes)',
    baseUrlLabel: null
  },
  gitlab: {
    label: 'GitLab',
    icon: 'GitlabOutlined',
    color: '#FC6D26',
    oauthLabel: 'Continue with GitLab OAuth',
    patLabel: 'GitLab Personal Access Token',
    patPlaceholder: 'glpat-xxxxxxxxxxxxxxxxxxxx',
    patHint: 'Profile > Preferences > Access Tokens > Create (api, read_repository scopes)',
    baseUrlLabel: 'GitLab Instance URL (self-hosted optional)'
  },
  bitbucket: {
    label: 'Bitbucket',
    icon: 'BitbucketOutlined',
    color: '#0052CC',
    oauthLabel: 'Continue with Bitbucket OAuth',
    patLabel: 'App Password',
    patPlaceholder: 'ATBBxxxxxxxxxxxxxxxxxxxx',
    patHint: 'Personal settings > App passwords > Create (Repository: Read & Write)',
    baseUrlLabel: null
  }
};

function GitProviderCrud({ form, handleFinish, initialValues, onOAuthConnect }) {
  const [selectedProvider, setSelectedProvider] = useState(initialValues?.provider || null);
  const [authMethod, setAuthMethod] = useState('oauth');
  const [isOAuthLoading, setOAuthLoading] = useState(false);

  const handleSubmit = useCallback((values) => {
    handleFinish(values);
  }, [handleFinish]);

  const handleOAuth = () => {
    if (!selectedProvider) {
      KoToast.error('Please select a Git provider first');
      return;
    }
    setOAuthLoading(true);
    ReleaseAPI.getOAuthUrl(selectedProvider).then(
      (data) => {
        setOAuthLoading(false);
        const w = 600, h = 700;
        const left = window.screenX + (window.innerWidth - w) / 2;
        const top = window.screenY + (window.innerHeight - h) / 2;
        const popup = window.open(
          data?.url,
          `${selectedProvider}_oauth`,
          `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
        );
        const onMessage = (event) => {
          if (event.data?.type === 'GIT_OAUTH_SUCCESS') {
            window.removeEventListener('message', onMessage);
            popup?.close();
            KoToast.success(`${PROVIDER_META[selectedProvider]?.label} connected successfully!`);
            onOAuthConnect && onOAuthConnect(event.data?.connection);
          }
          if (event.data?.type === 'GIT_OAUTH_ERROR') {
            window.removeEventListener('message', onMessage);
            popup?.close();
            KoToast.error('OAuth failed. Please try again or use a token.');
          }
        };
        window.addEventListener('message', onMessage);
      },
      () => {
        setOAuthLoading(false);
        KoToast.error('Failed to get OAuth URL. Please use token instead.');
      }
    );
  };

  const ProviderWidget = ({ value, onChange }) => (
    <div className={styles.providerGrid}>
      {Object.entries(PROVIDER_META).map(([key, meta]) => (
        <div
          key={key}
          className={`${styles.providerCard} ${value === key ? styles.providerCardActive : ''}`}
          onClick={() => { onChange(key); setSelectedProvider(key); }}
        >
          <KoIcon name={meta.icon} width={24} height={24} />
          <KoText text={meta.label} className={styles.providerLabel} />
        </div>
      ))}
    </div>
  );

  const AuthMethodWidget = ({ value, onChange }) => (
    <div className={styles.authMethodRow}>
      {[
        { key: 'oauth', label: 'OAuth (Recommended)' },
        { key: 'pat', label: 'Personal Access Token' }
      ].map((opt) => (
        <div
          key={opt.key}
          className={`${styles.authMethodBtn} ${(value || authMethod) === opt.key ? styles.authMethodBtnActive : ''}`}
          onClick={() => { onChange(opt.key); setAuthMethod(opt.key); }}
        >
          <KoText text={opt.label} />
        </div>
      ))}
    </div>
  );

  const currentProvider = selectedProvider ? PROVIDER_META[selectedProvider] : null;

  const patFields = selectedProvider && authMethod === 'pat' ? [
    ...(currentProvider.baseUrlLabel ? [{
      key: 'baseUrl',
      label: currentProvider.baseUrlLabel,
      colSpan: 4,
      widget: KoInput,
      widgetProps: { placeholder: 'https://gitlab.yourcompany.com' }
    }] : []),
    {
      key: 'username',
      label: 'Username / Workspace',
      colSpan: 2,
      widget: KoInput,
      widgetProps: { placeholder: 'your-username' }
    },
    {
      key: 'token',
      label: currentProvider.patLabel,
      colSpan: 2,
      widget: KoInput,
      widgetProps: {
        type: 'password',
        placeholder: currentProvider.patPlaceholder
      },
      tooltip: currentProvider.patHint
    }
  ] : [];

  const meta = {
    columns: 4,
    formItemLayout: null,
    initialValues,
    colon: true,
    fields: [
      {
        key: 'provider',
        label: 'Select Git Provider',
        colSpan: 4,
        widget: ProviderWidget,
        forwardRef: true,
        required: true,
        rules: [{ required: true, message: 'Please select a provider' }]
      },
      {
        key: 'authMethod',
        label: 'Authentication Method',
        colSpan: 4,
        widget: AuthMethodWidget,
        forwardRef: true,
        initialValue: 'oauth'
      },
      {
        key: 'connectionName',
        label: 'Connection Name',
        colSpan: 4,
        widget: KoInput,
        widgetProps: {
          placeholder: `e.g. Company GitHub, ${currentProvider?.label || 'Provider'} - Engineering`
        }
      },
      ...patFields
    ]
  };

  return (
    <div className={styles.wrapper}>
      <KoForm
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        onSubmit={handleSubmit}
      >
        <KoFormBuilder form={form} meta={meta} />

        {selectedProvider && authMethod === 'oauth' && (
          <div className={styles.oauthSection}>
            <KoButton
              type='primary'
              loading={isOAuthLoading}
              icon={<KoIcon name={currentProvider.icon} color='#FFF' width={16} height={16} />}
              label={currentProvider.oauthLabel}
              onClick={handleOAuth}
              className={styles.oauthBtn}
            />
            <KoText
              className={styles.oauthHint}
              text={`You'll be redirected to ${currentProvider.label} to authorize access. Only repository read/write permissions are requested.`}
            />
          </div>
        )}
      </KoForm>
    </div>
  );
}

export default GitProviderCrud;
